import React, { useEffect, useMemo, useState, lazy, Suspense, MouseEvent } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Menu,
  MenuItem,
} from '@mui/material';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { useNavigate } from 'react-router-dom';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HomeIcon from '@mui/icons-material/Home';
import { dateFormate, generateFilterKey } from '../../utils/helper.jsx';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import SearchBox from '../FlowBuilder/Common/SearchBox/index.tsx';
import Pagination from '../FlowBuilder/Common/Pagination/index.tsx';
import CommonTable from '../Shared/Tables/TableV1.tsx';
import { campaignTableStateAtom, userInformationAtom } from '../../Services/_states/index.js';
import { fetchCampaignListSelector } from '../../Services/_states/CampaignPlanning/campaignSelector.ts';
import CreateEditDispositionModal from './DispositionModal.tsx';
import useDebounce from '../Shared/Delay/index.tsx';


const SendEmailModal = lazy(() => import('./SendEmailModal.tsx'));
const ViewCommentModal = lazy(() => import('./ViewCommentModal.tsx'));

const CampaignList = () => {
  const navigate = useNavigate();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const [openDispositionModal, setOpenDispositionModal] = useState(false);
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [openCommentModal, setOpenCommentModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState({});
  const [tableState, setTableState] = useRecoilState(campaignTableStateAtom);
  const fetchedData = useRecoilValueLoadable(fetchCampaignListSelector);
  const [searchQuery, setSearchQuery] = useState(tableState?.filters?.search || '');
  const debouncedSearch = useDebounce(searchQuery, 500);

  const defaultColumns = [
    'Brand Type',
    'Assigned To',
    'Assigned By',
    'Assigned Date',
    'Start Date',
    'End Date',
    'Campaign Status',
  ];

  const [selectedColumns, setSelectedColumns] = useState<string[]>(defaultColumns);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (fetchedData.state === 'hasValue' && !fetchedData.contents.fromCache) {
      const filterKey = generateFilterKey(tableState.filters);
      setTableState((prev) => ({
        ...prev,
        cache: { ...prev.cache, [filterKey]: fetchedData.contents.data },
        totalCountCache: { ...prev.totalCountCache, [filterKey]: fetchedData.contents.totalCount },
      }));
    }
  }, [fetchedData, setTableState]);

  useEffect(() => {
    if (debouncedSearch !== tableState.filters.search) {
      setTableState((prev) => ({
        ...prev,
        filters: { ...prev.filters, search: debouncedSearch, page: 0 },
        cache: {},
      }));
    }
  }, [debouncedSearch, setTableState]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setTableState((prev) => ({
      ...prev,
      filters: { ...prev.filters, page: newPage },
    }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10) || 10;
    setTableState((prev) => ({
      ...prev,
      filters: { ...prev.filters, limit: value, page: 0 },
    }));
  };

  const handleOpenPopover = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleToggleColumn = (column: string) => {
    setSelectedColumns((prev) =>
      prev.includes(column) ? prev.filter((c) => c !== column) : [...prev, column]
    );
  };

  const handleSelectAllColumns = () => {
    if (selectedColumns.length === defaultColumns.length) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(defaultColumns);
    }
  };

  const columns = useMemo(() => {
    const cols = [];

    // 🔒 Always Show: Sr No
    cols.push({
      accessorKey: 'index',
      header: 'Sr No',
      size: 30,
      Cell: ({ row, table }: any) => {
        const rowIndex = row.index;
        const page = tableState?.filters?.page;
        return page * tableState?.filters?.limit + rowIndex + 1;
      },
    });

    // 🔒 Always Show: Name
    cols.push({ accessorKey: 'name', header: 'Name' });

    // 🔒 Always Show: Organisation Name / Account (with width)
    cols.push({
      accessorKey: 'accountName',
      header: 'Organisation Name / Account',
      size: 300,
      Cell: ({ row }: any) => (
        <Box sx={{ whiteSpace: 'nowrap' }}>
          {row.original.accountOrganisationName}
          {row.original.accountName && <> / {row.original.accountName}</>}
        </Box>
      ),
    });

    // ✅ Selectable Columns
    if (selectedColumns.includes('Brand Type')) {
      cols.push({ accessorKey: 'brand', header: 'Brand Type' });
    }
    if (selectedColumns.includes('Assigned To')) {
      cols.push({
        accessorKey: 'assigned_to',
        header: 'Assigned To',
        Cell: ({ row }: any) => {
          const assignedList = row.original?.assigned?.map((item: any) => item?.assigned_to).filter(Boolean) || [];
          return (
            <Box>
              {assignedList.slice(0, 3).map((name: any, index: number) => (
                <div key={index}>{name}</div>
              ))}
              {assignedList.length > 3 && (
                <Box sx={{ color: 'blue', cursor: 'pointer' }}>+{assignedList.length - 3} more</Box>
              )}
            </Box>
          );
        },
      });
    }
    if (selectedColumns.includes('Assigned By')) {
      cols.push({
        accessorKey: 'assigned_by',
        header: 'Assigned By',
        Cell: ({ row }: any) => {
          const assignedList = row.original?.assigned?.map((item: any) => item?.assigned_by).filter(Boolean) || [];
          return (
            <Box>
              {assignedList.slice(0, 3).map((name: any, index: number) => (
                <div key={index}>{name}</div>
              ))}
              {assignedList.length > 3 && (
                <Box sx={{ color: 'blue', cursor: 'pointer' }}>+{assignedList.length - 3} more</Box>
              )}
            </Box>
          );
        },
      });
    }
    if (selectedColumns.includes('Assigned Date')) {
      cols.push({
        accessorKey: 'created_at',
        header: 'Assigned Date',
        Cell: ({ row }: any) => dateFormate(row.original.created_at, 'DD-MM-YYYY'),
      });
    }
    if (selectedColumns.includes('Start Date')) {
      cols.push({
        accessorKey: 'tentative_start_date',
        header: 'Start Date',
        Cell: ({ row }: any) => dateFormate(row.original?.campaign?.tentative_start_date, 'DD-MM-YYYY'),
      });
    }
    if (selectedColumns.includes('End Date')) {
      cols.push({
        accessorKey: 'tentative_end_date',
        header: 'End Date',
        Cell: ({ row }: any) => dateFormate(row.original?.campaign?.tentative_end_date, 'DD-MM-YYYY'),
      });
    }
    if (selectedColumns.includes('Campaign Status')) {
      cols.push({ accessorKey: 'status', header: 'Campaign Status' });
    }

    // ⛔️ Always Show: Action
    cols.push({
      accessorKey: 'action',
      header: 'Action',
      enableSorting: false,
      size: 60,
      Cell: ({ row }: any) => {
        const [anchorElAction, setAnchorElAction] = React.useState<null | HTMLElement>(null);
        const openAction = Boolean(anchorElAction);

        const handleClick = (event: MouseEvent<HTMLElement>) => setAnchorElAction(event.currentTarget);
        const handleClose = () => setAnchorElAction(null);

        return (
          <Box>
            <IconButton onClick={handleClick}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              id={`menu-${row.index}`}
              anchorEl={anchorElAction}
              open={openAction}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': `menu-button-${row.index}`,
              }}
            >
              <MenuItem
                onClick={() => {
                  setSelectedCampaign(row.original);
                  setOpenCommentModal(true);
                  handleClose();
                }}
              >
                Comment
              </MenuItem>
              {/* <MenuItem
                onClick={() =>
                  navigate(`/r/campaign/${row.original?.proposal_id}/booking-plan`, {
                    state: {
                      campaignId: row.original?.proposal_id,
                      campaignName: row.original?.name,
                      bdOwner: row.original?.created_by,
                      status: row.original?.status,
                    },
                  })
                }
              >
                Booking Plan
              </MenuItem> */}
              <MenuItem onClick={() => (window.location.href = `/#/${row.original?.proposal_id}/releasePlan`)}>
                Booking plan
              </MenuItem>
              <MenuItem onClick={() => (window.location.href = `/#/${row.original?.proposal_id}/auditReleasePlan`)}>
                Assign dates
              </MenuItem>
              <MenuItem onClick={() => (window.location.href = `/#/${row.original?.proposal_id}/opsExecutionPlan`)}>
                Image details
              </MenuItem>
              <MenuItem onClick={handleClose}>Download</MenuItem>
              <MenuItem
                onClick={() => {
                  setOpenEmailModal(true);
                  handleClose();
                }}
              >
                Send Email
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setOpenDispositionModal(true);
                  handleClose();
                }}
              >
                Disposition
              </MenuItem>
            </Menu>
          </Box>
        );
      },
    });

    return cols;
  }, [selectedColumns, tableState]);

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding:"5px" }}>
      <DynamicBreadcrumbs items={[{ label: <IconButton color="primary"><HomeIcon /></IconButton>, onClick: () => navigate('/') }, { label: ' Business Services' }]} />
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item>
          <Typography variant="h5" fontWeight="bold">
            Business Services
          </Typography>
        </Grid>
        <Grid item>
          <Button variant="outlined" size='small' onClick={handleOpenPopover}>
            Select Columns
          </Button>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClosePopover}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Box sx={{ p: 2, maxHeight: 300, width: 250, overflowY: 'auto' }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedColumns.length === defaultColumns.length}
                      indeterminate={selectedColumns.length > 0 && selectedColumns.length < defaultColumns.length}
                      onChange={handleSelectAllColumns}
                    />
                  }
                  label={<Typography fontWeight="bold">Select All</Typography>}
                />
                {defaultColumns.map((column) => (
                  <FormControlLabel
                    key={column}
                    control={
                      <Checkbox
                        checked={selectedColumns.includes(column)}
                        onChange={() => handleToggleColumn(column)}
                      />
                    }
                    label={column}
                  />
                ))}
              </FormGroup>
            </Box>
          </Popover>
        </Grid>
      </Grid>

      <CommonTable
        columns={columns}
        data={tableState.cache[generateFilterKey(tableState.filters)] || []}
        state={{ isLoading: fetchedData.state === 'loading' }}
        muiTableContainerProps={{
          sx: {
            height: 'calc(100vh - 300px)',
            width: '100%',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '6px' },
          },
        }}
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
            <Pagination
              totalCount={tableState.totalCountCache[generateFilterKey(tableState.filters)] || 0}
              rowsPerPage={tableState?.filters?.limit}
              page={tableState?.filters?.page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        )}
      />
      <CreateEditDispositionModal
        open={openDispositionModal}
        editMode={false}
        onClose={(data) => {
          setOpenDispositionModal(false);
          if (data) {
            console.log('Created new disposition:', data);
          }
        }}
      />
      <Suspense fallback={<div>Loading...</div>}>
        {openEmailModal && (
          <SendEmailModal
            open={openEmailModal}
            onClose={() => setOpenEmailModal(false)}
            onSend={(type, emails, isTest) => {
              console.log('Sending:', { type, emails, isTest });
              setOpenEmailModal(false);
            }}
          />
        )}
        {openCommentModal && (
          <ViewCommentModal open={openCommentModal} onClose={() => setOpenCommentModal(false)} campaign={selectedCampaign} />
        )}
      </Suspense>
    </Box>
  );
};

export default CampaignList;
